import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminApi } from '@/hooks/useAdminApi';
import { Building2, CheckCircle, Loader2, MapPin, Search, Users } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AffiliateDialogs } from './admin-affiliates/AffiliateDialogs';
import {
  mapPendingAffiliate,
  affiliateStatusColors as statusColors,
  affiliateStatusLabels as statusLabels,
} from './admin-affiliates/model';
import type { Affiliate, AffiliateAgent, AffiliateOwnerOption } from './admin-affiliates/model';

export default function AdminAffiliates() {
  const adminApi = useAdminApi();
  const adminApiRef = useRef(adminApi);
  adminApiRef.current = adminApi;

  const [searchParams, setSearchParams] = useSearchParams();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState({
    total: 0,
    pending: 0,
    active: 0,
    cancelled: 0,
    past_due: 0,
  });

  // Modal state
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [approveAgentId, setApproveAgentId] = useState('');
  const [agentsList, setAgentsList] = useState<AffiliateAgent[]>([]);
  const [ownerOptions, setOwnerOptions] = useState<AffiliateOwnerOption[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [newAgentEmail, setNewAgentEmail] = useState('');
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentTerritory, setNewAgentTerritory] = useState('');
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState<
    Partial<Affiliate & { agent_id?: string | null; owner_user_id?: string | null }>
  >({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create gym modal state
  const [showCreateGymModal, setShowCreateGymModal] = useState(false);
  const [newGymName, setNewGymName] = useState('');
  const [newGymAddress, setNewGymAddress] = useState('');
  const [newGymPhone, setNewGymPhone] = useState('');
  const [newGymEmail, setNewGymEmail] = useState('');
  const [newGymAgentId, setNewGymAgentId] = useState('');
  const [isCreatingGym, setIsCreatingGym] = useState(false);

  // Update URL when status filter changes
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    if (value) {
      setSearchParams({ status: value });
    } else {
      setSearchParams({});
    }
  };

  const fetchAffiliates = useCallback(
    async (cursor?: string) => {
      try {
        setIsLoading(true);
        setError(null);

        if (statusFilter === 'pending') {
          const [response, countsResponse] = await Promise.all([
            adminApiRef.current.getPendingAffiliates(),
            adminApiRef.current.getAffiliates({ limit: 1 }),
          ]);

          if (!response.success || !response.data) {
            setError(response.error || 'Failed to load pending affiliates');
            return;
          }

          const pendingData = response.data;
          const query = searchQuery.trim().toLowerCase();
          const pendingAffiliates: Affiliate[] = pendingData.affiliates
            .map(mapPendingAffiliate)
            .filter(
              (affiliate) =>
                !query ||
                affiliate.name.toLowerCase().includes(query) ||
                affiliate.owner.name.toLowerCase().includes(query) ||
                affiliate.owner.email.toLowerCase().includes(query) ||
                affiliate.contact_email?.toLowerCase().includes(query)
            );

          setAffiliates(pendingAffiliates);
          setHasMore(false);
          setNextCursor(null);
          setStatusCounts({
            total: countsResponse.data?.counts?.total ?? 0,
            pending: pendingData.pending_count,
            active: countsResponse.data?.counts?.active ?? 0,
            cancelled: countsResponse.data?.counts?.cancelled ?? 0,
            past_due: countsResponse.data?.counts?.past_due ?? 0,
          });
          return;
        }

        const response = await adminApiRef.current.getAffiliates({
          status: statusFilter || undefined,
          search: searchQuery || undefined,
          cursor,
          limit: 50,
        });

        if (response.success && response.data) {
          const data = response.data;
          if (cursor) {
            setAffiliates((prev) => [...prev, ...data.affiliates]);
          } else {
            setAffiliates(data.affiliates);
          }
          setHasMore(data.pagination.has_more);
          setNextCursor(data.pagination.next_cursor);
          if (data.counts) {
            setStatusCounts(data.counts);
          }

          const pendingResponse = await adminApiRef.current.getPendingAffiliates();
          if (pendingResponse.success && pendingResponse.data) {
            const pendingData = pendingResponse.data;
            setStatusCounts((counts) => ({
              ...counts,
              pending: pendingData.pending_count,
            }));
          }
        } else {
          setError(response.error || 'Failed to load affiliates');
        }
      } catch (err) {
        console.error('Error fetching affiliates:', err);
        setError(err instanceof Error ? err.message : 'Failed to load affiliates');
      } finally {
        setIsLoading(false);
      }
    },
    [statusFilter, searchQuery]
  );

  // Fetch affiliates on mount and when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAffiliates();
    }, 300); // Debounce search

    return () => clearTimeout(timer);
  }, [fetchAffiliates]);

  const handleView = (affiliate: Affiliate) => {
    setSelectedAffiliate(affiliate);
    setShowDetailModal(true);
  };

  const handleApproveClick = async (affiliate: Affiliate) => {
    setSelectedAffiliate(affiliate);
    setApproveAgentId(affiliate.agent?.id || '');
    setShowApproveModal(true);

    // Fetch agents list for the dropdown
    if (agentsList.length === 0) {
      setIsLoadingAgents(true);
      try {
        const response = await adminApi.getAgents({ limit: 100 });
        if (response.success && response.data) {
          setAgentsList(response.data.agents);
        }
      } catch {
        // Non-critical — dropdown will just be empty
      } finally {
        setIsLoadingAgents(false);
      }
    }
  };

  const handleRejectClick = (affiliate: Affiliate) => {
    setSelectedAffiliate(affiliate);
    setShowRejectModal(true);
    setRejectReason('');
  };

  const handleApprove = async () => {
    if (!selectedAffiliate) return;
    try {
      setIsProcessing(true);
      const response = await adminApi.approveAffiliate(
        selectedAffiliate.id,
        approveAgentId || undefined
      );
      if (response.success) {
        toast.success('Affiliate approved successfully!');
        setShowApproveModal(false);
        setSelectedAffiliate(null);
        fetchAffiliates();
      } else {
        toast.error(response.error || 'Failed to approve affiliate');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve affiliate');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateAgent = async () => {
    if (!newAgentEmail.trim()) {
      toast.error('Agent email is required');
      return;
    }
    setIsCreatingAgent(true);
    try {
      const response = await adminApi.inviteUser({
        email: newAgentEmail.trim(),
        name: newAgentName.trim() || undefined,
        role: 'agent',
        territory_name: newAgentTerritory.trim() || undefined,
      });
      if (response.success && response.data) {
        toast.success('Agent created! Refreshing list...');
        // Refetch agents list and select the new one
        const agentsResponse = await adminApi.getAgents({ limit: 100 });
        if (agentsResponse.success && agentsResponse.data) {
          setAgentsList(agentsResponse.data.agents);
          // Find the newly created agent by matching user_id or name
          const newAgent = agentsResponse.data.agents.find(
            (a) =>
              a.name.toLowerCase() === (newAgentName.trim() || newAgentEmail.trim()).toLowerCase()
          );
          if (newAgent) {
            setApproveAgentId(newAgent.id);
          }
        }
        setShowCreateAgent(false);
        setNewAgentEmail('');
        setNewAgentName('');
        setNewAgentTerritory('');
      } else {
        toast.error(response.error || 'Failed to create agent');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create agent');
    } finally {
      setIsCreatingAgent(false);
    }
  };

  const handleReject = async () => {
    if (!selectedAffiliate) return;
    try {
      setIsProcessing(true);
      const response = await adminApi.rejectAffiliate(
        selectedAffiliate.id,
        rejectReason || undefined
      );
      if (response.success) {
        toast.success('Affiliate application rejected');
        setShowRejectModal(false);
        setSelectedAffiliate(null);
        setRejectReason('');
        fetchAffiliates();
      } else {
        toast.error(response.error || 'Failed to reject affiliate');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reject affiliate');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedAffiliate) return;
    try {
      setIsProcessing(true);
      const response = await adminApi.suspendAffiliate(selectedAffiliate.id);
      if (response.success) {
        toast.success('Affiliate suspended');
        setShowDetailModal(false);
        setSelectedAffiliate(null);
        fetchAffiliates();
      } else {
        toast.error(response.error || 'Failed to suspend affiliate');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to suspend affiliate');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleActivate = async () => {
    if (!selectedAffiliate) return;
    try {
      setIsProcessing(true);
      const response = await adminApi.activateAffiliate(selectedAffiliate.id);
      if (response.success) {
        toast.success('Affiliate activated successfully');
        setShowDetailModal(false);
        setSelectedAffiliate(null);
        fetchAffiliates();
      } else {
        toast.error(response.error || 'Failed to activate affiliate');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to activate affiliate');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditClick = () => {
    if (!selectedAffiliate) return;
    setEditData({
      name: selectedAffiliate.name,
      address: selectedAffiliate.address || '',
      phone: selectedAffiliate.phone || '',
      whatsapp: selectedAffiliate.whatsapp || '',
      contact_email: selectedAffiliate.contact_email || '',
      country_code: selectedAffiliate.country_code || '',
      agent_id: selectedAffiliate.agent?.id || null,
      owner_user_id: selectedAffiliate.owner?.id || null,
    });
    setIsEditMode(true);
    if (agentsList.length === 0 || ownerOptions.length === 0) {
      setIsLoadingAgents(true);
      const agentsRequest =
        agentsList.length === 0
          ? adminApiRef.current.getAgents({ limit: 100 })
          : Promise.resolve(null);
      const ownersRequest =
        ownerOptions.length === 0
          ? (async () => {
              const owners: AffiliateOwnerOption[] = [];
              let cursor: string | undefined;
              do {
                const response = await adminApiRef.current.getUsers({
                  role: 'affiliate_owner',
                  limit: 100,
                  cursor,
                });
                if (!response.success || !response.data) break;
                owners.push(
                  ...response.data.users.map(({ id, name, email }) => ({ id, name, email }))
                );
                cursor = response.data.pagination.has_more
                  ? response.data.pagination.next_cursor || undefined
                  : undefined;
              } while (cursor);
              return owners.sort((a, b) => a.name.localeCompare(b.name));
            })()
          : Promise.resolve(null);

      Promise.allSettled([
        agentsRequest.then((response) => {
          if (response?.success && response.data) {
            setAgentsList(response.data.agents);
          }
        }),
        ownersRequest.then((owners) => {
          if (owners) {
            setOwnerOptions(owners);
          }
        }),
      ]).finally(() => setIsLoadingAgents(false));
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedAffiliate) return;
    setIsSaving(true);
    try {
      const updates: Record<string, string | null | undefined> = {};
      if (editData.name !== selectedAffiliate.name) updates.name = editData.name as string;
      if (editData.address !== (selectedAffiliate.address || ''))
        updates.address = editData.address as string;
      if (editData.phone !== (selectedAffiliate.phone || ''))
        updates.phone = editData.phone as string;
      if (editData.whatsapp !== (selectedAffiliate.whatsapp || ''))
        updates.whatsapp = editData.whatsapp as string;
      if (editData.contact_email !== (selectedAffiliate.contact_email || ''))
        updates.contact_email = editData.contact_email as string;
      if (editData.country_code !== (selectedAffiliate.country_code || ''))
        updates.country_code = editData.country_code as string;
      if (editData.agent_id !== (selectedAffiliate.agent?.id || null))
        updates.agent_id = editData.agent_id as string | null;
      if (editData.owner_user_id !== (selectedAffiliate.owner?.id || null))
        updates.owner_user_id = editData.owner_user_id as string | null;

      if (Object.keys(updates).length === 0) {
        setIsEditMode(false);
        return;
      }

      const response = await adminApiRef.current.updateAffiliate(selectedAffiliate.id, updates);
      toast.success(response.data?.message || 'Affiliate updated');
      setIsEditMode(false);
      setShowDetailModal(false);
      fetchAffiliates();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update affiliate';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAffiliate = async () => {
    if (!selectedAffiliate) return;
    if (deleteConfirmName !== selectedAffiliate.name) {
      toast.error('Please type the gym name to confirm deletion');
      return;
    }
    setIsDeleting(true);
    try {
      const response = await adminApiRef.current.deleteAffiliate(selectedAffiliate.id);
      if (response.data?.has_active_subscription) {
        toast.warning(
          'Gym deleted, but it had an active Stripe subscription. Cancel it in Stripe dashboard.'
        );
      } else {
        toast.success(response.data?.message || 'Gym deleted');
      }
      setShowDeleteConfirm(false);
      setShowDetailModal(false);
      setSelectedAffiliate(null);
      fetchAffiliates();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete affiliate';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateGym = async () => {
    // AFF-2: name, address, phone, email are all required
    if (!newGymName.trim()) {
      toast.error('Gym name is required');
      return;
    }
    if (!newGymAddress.trim()) {
      toast.error('Address is required');
      return;
    }
    if (!newGymPhone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!newGymEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newGymEmail.trim())) {
      toast.error('A valid contact email is required');
      return;
    }

    setIsCreatingGym(true);
    try {
      const response = await adminApiRef.current.createAffiliate({
        name: newGymName.trim(),
        address: newGymAddress.trim(),
        phone: newGymPhone.trim(),
        email: newGymEmail.trim(),
        agent_id: newGymAgentId || undefined,
      });
      if (response.success) {
        toast.success(response.message || `Gym "${newGymName.trim()}" created`);
        setShowCreateGymModal(false);
        setNewGymName('');
        setNewGymAddress('');
        setNewGymPhone('');
        setNewGymEmail('');
        setNewGymAgentId('');
        fetchAffiliates();
      } else {
        toast.error(response.error || 'Failed to create gym');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create gym');
    } finally {
      setIsCreatingGym(false);
    }
  };

  // Stats counts from API (unfiltered totals)
  const totalCount = statusCounts.total;
  const pendingCount = statusCounts.pending;
  const activeCount = statusCounts.active;

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Affiliate Management</h1>
          <p className="mt-1 text-muted-foreground">
            Review and manage affiliate gyms and applications.
          </p>
        </div>
        <Button
          className="gap-2"
          onClick={async () => {
            setNewGymName('');
            setNewGymAddress('');
            setNewGymPhone('');
            setNewGymEmail('');
            setNewGymAgentId('');
            setShowCreateGymModal(true);
            // Fetch agents if not already loaded
            if (agentsList.length === 0) {
              setIsLoadingAgents(true);
              try {
                const response = await adminApi.getAgents({ limit: 100 });
                if (response.success && response.data) {
                  setAgentsList(response.data.agents);
                }
              } catch {
                // Non-critical
              } finally {
                setIsLoadingAgents(false);
              }
            }
          }}
        >
          <Building2 className="h-4 w-4" />
          Add Gym
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <Card
          className={`cursor-pointer transition-colors ${statusFilter === '' ? 'ring-2 ring-primary' : 'hover:bg-muted/50'}`}
          onClick={() => handleStatusFilterChange('')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-blue-500/10 p-2">
                <Building2 className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">All Affiliates</p>
                <p className="text-2xl font-bold">{totalCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-colors ${statusFilter === 'pending' ? 'ring-2 ring-primary' : 'hover:bg-muted/50'}`}
          onClick={() => handleStatusFilterChange('pending')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-orange-500/10 p-2">
                <Building2 className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-colors ${statusFilter === 'active' ? 'ring-2 ring-primary' : 'hover:bg-muted/50'}`}
          onClick={() => handleStatusFilterChange('active')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-green-500/10 p-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter Bar */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by gym name, owner, email..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">Status:</Label>
              <Select
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                className="w-40"
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'active', label: 'Active' },
                  { value: 'cancelled', label: 'Suspended' },
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Affiliates Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading && affiliates.length === 0 ? (
            <div className="flex h-[300px] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="flex h-[300px] flex-col items-center justify-center gap-4">
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={() => fetchAffiliates()}>
                Retry
              </Button>
            </div>
          ) : affiliates.length === 0 ? (
            <div className="flex h-[300px] flex-col items-center justify-center text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Building2 className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No affiliates found</h3>
              <p className="text-muted-foreground max-w-md">
                {searchQuery || statusFilter
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No affiliate applications yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Gym Name</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="hidden md:table-cell">Location</TableHead>
                    <TableHead className="hidden lg:table-cell">Agent</TableHead>
                    <TableHead className="hidden sm:table-cell">Members</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affiliates.map((affiliate) => (
                    <TableRow key={affiliate.id} className="animate-fade-in">
                      <TableCell>
                        <div className="font-medium">{affiliate.name}</div>
                        <div className="text-sm text-muted-foreground md:hidden">
                          {affiliate.address || affiliate.country_code || '—'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>{affiliate.owner?.name || 'Unassigned'}</div>
                        <div className="text-sm text-muted-foreground">
                          {affiliate.owner?.email || '—'}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {affiliate.address || affiliate.country_code || '—'}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {affiliate.agent ? (
                          <div>
                            <div className="text-sm">{affiliate.agent.name}</div>
                            {affiliate.agent.territory_name && (
                              <div className="text-xs text-muted-foreground">
                                {affiliate.agent.territory_name}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-muted-foreground" />
                          {affiliate.member_count}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge
                            variant="outline"
                            className={
                              statusColors[affiliate.subscription_status] ||
                              'bg-gray-500/10 text-gray-400'
                            }
                          >
                            {statusLabels[affiliate.subscription_status] ||
                              affiliate.subscription_status}
                          </Badge>
                          {affiliate.recommended_by && (
                            <Badge
                              variant="secondary"
                              className="bg-primary/10 text-primary text-xs"
                            >
                              Recommended by {affiliate.recommended_by.name}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleView(affiliate)}>
                            View
                          </Button>
                          {affiliate.subscription_status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-500 hover:text-green-600"
                                onClick={() => handleApproveClick(affiliate)}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleRejectClick(affiliate)}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>

        {/* Load More */}
        {hasMore && (
          <div className="border-t p-4 text-center">
            <Button
              variant="outline"
              onClick={() => fetchAffiliates(nextCursor || undefined)}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Load More
            </Button>
          </div>
        )}
      </Card>

      <AffiliateDialogs
        controller={{
          selectedAffiliate,
          showDetailModal,
          showDeleteConfirm,
          showApproveModal,
          showRejectModal,
          showCreateGymModal,
          isEditMode,
          editData,
          agentsList,
          ownerOptions,
          isSaving,
          isProcessing,
          isDeleting,
          isLoadingAgents,
          deleteConfirmName,
          approveAgentId,
          showCreateAgent,
          newAgentEmail,
          newAgentName,
          newAgentTerritory,
          isCreatingAgent,
          rejectReason,
          newGymName,
          newGymAddress,
          newGymPhone,
          newGymEmail,
          newGymAgentId,
          isCreatingGym,
          setShowDetailModal,
          setShowDeleteConfirm,
          setShowApproveModal,
          setShowRejectModal,
          setShowCreateGymModal,
          setIsEditMode,
          setEditData,
          setDeleteConfirmName,
          setApproveAgentId,
          setShowCreateAgent,
          setNewAgentEmail,
          setNewAgentName,
          setNewAgentTerritory,
          setRejectReason,
          setNewGymName,
          setNewGymAddress,
          setNewGymPhone,
          setNewGymEmail,
          setNewGymAgentId,
          handleEditClick,
          handleSaveEdit,
          handleDeleteAffiliate,
          handleApprove,
          handleCreateAgent,
          handleReject,
          handleSuspend,
          handleActivate,
          handleCreateGym,
        }}
      />
    </DashboardLayout>
  );
}
